import { ChatMessageDTO, SystemMessageDTO } from './chat-types';
import { AutoTopUpSettingsDto } from './dto/table/auto-top-up-settings';
import { GlobalSettingsUpdatedEvent, TournamentSettingUpdatedEvent } from './event-types';
import { ActiveLeaderboardOverview } from './leaderboard-types';
import {
  LobbyAlertUpdatedPayload,
  LobbyCancelSeatReadyPayload,
  LobbyDuplicateLoginPayload,
  LobbyJoinLeaderboardRoomSuccessPayload,
  LobbyMaintenanceConfigUpdatedPayload,
  LobbyTableUrlUpdatedPayload,
  LobbyTournamentSettingDeletedPayload,
  LobbyUserTournamentDollarsAddedPayload,
  LobbyUserTournamentTicketAddedPayload,
  TableActionPerformedPayload,
  TableAddingChipsEndPayload,
  TableAddingChipsPayload,
  TableChangeTablePayload,
  TableClientVersionOutdatedPayload,
  TableConfirmPayload,
  TableErrorPayload,
  TableHandFinishedPayload,
  TableLeftPayload,
  TableMyTurnPayload,
  TablePlayerTurnPayload,
  TableRebuyProcessedPayload,
  TableRunoutAgreementRequestPayload,
  TableRunoutCountRequestPayload,
  TableRunoutWaitingPayload,
  TableSatelliteTournamentEndedPayload,
  TableSeatReservedPayload,
  TableSitPayload,
  TableStatePayload,
  TableTournamentBreakPayload,
  TableTournamentCancelledPayload,
  TableTournamentEndedPayload,
  TableTournamentLevelChangePayload,
  TableTournamentPausedPayload,
  TableTournamentResumedPayload,
  TableTournamentResumingAtPayload,
  TournamentCancelledPayload,
  TournamentCreatedPayload,
  TournamentEndedPayload,
  TournamentLevelChangePayload,
  TournamentPlayerEliminatedPayload,
  TournamentRegistrationSuccessPayload,
  TournamentStartPayload,
  TournamentStatsLobbyPayload,
  TournamentUnregistrationSuccessPayload,
} from './socket-events-payloads';
import { TableJoinedDto } from './table-state-types';
import { SeatReadyEventTableInfo } from './table-types';

type SocketEventType =
  | LobbyClientOutgoingEvents
  | LobbyClientIncomingEvents
  | TournamentsClientIncomingEvents
  | SharedTournamentIncomingEvents
  | ChatClientIncomingEvents
  | ChatClientOutgoingEvents
  | TableClientOutgoingEvents
  | TableClientIncomingEvents;

interface EmitEvent<T extends SocketEventType, U> {
  type: T;
  payload: U;
}

interface EmitTableEvent<
  T extends
    | TableClientIncomingEvents
    | ChatClientIncomingEvents
    | SharedTournamentIncomingEvents
    | TournamentsClientIncomingEvents,
  U,
> extends EmitEvent<T, U> {
  tableId: string;
}

interface EmitUserEvent<
  T extends LobbyClientIncomingEvents | TableClientIncomingEvents | TournamentsClientIncomingEvents,
  U,
> extends EmitEvent<T, U> {
  userId: string;
}

interface EmitUserTableEvent<T extends TableClientIncomingEvents | ChatClientIncomingEvents, U>
  extends EmitEvent<T, U> {
  tableId: string;
  userId: string;
}

interface EmitTournamentRoomEvent<T extends TournamentsClientIncomingEvents | SharedTournamentIncomingEvents, U>
  extends EmitEvent<T, U> {
  tournamentId: string;
}

type UserTableEventPayloads = {
  [ChatClientIncomingEvents.NEW_MESSAGE]: ChatMessageDTO;
  [TableClientIncomingEvents.CLIENT_VERSION_OUTDATED]: TableClientVersionOutdatedPayload;
  [TableClientIncomingEvents.TABLE_SEAT_READY]: SeatReadyEventTableInfo;
  [TableClientIncomingEvents.TABLE_STATE]: TableStatePayload;
  [TableClientIncomingEvents.SEAT_RESERVED]: TableSeatReservedPayload;
  [TableClientIncomingEvents.TABLE_JOINED]: TableJoinedDto;
  [TableClientIncomingEvents.REBUY_PROCESSED]: TableRebuyProcessedPayload;
  [TableClientIncomingEvents.AUTO_TOP_UP_BALANCE_UPDATED]: Partial<AutoTopUpSettingsDto>;
  [TableClientIncomingEvents.RUNOUT_WAITING]: TableRunoutWaitingPayload;
  [TableClientIncomingEvents.CHANGE_TABLE]: TableChangeTablePayload;
};

export type UserTableEvent = {
  [EventType in keyof UserTableEventPayloads]: EmitUserTableEvent<EventType, UserTableEventPayloads[EventType]>;
}[keyof UserTableEventPayloads];

type TableEventPayloads = {
  [ChatClientIncomingEvents.SYSTEM_MESSAGE]: SystemMessageDTO;
  [TableClientIncomingEvents.TABLE_STATE]: TableStatePayload;
  [TableClientIncomingEvents.TABLE_LEFT]: TableLeftPayload;
  [TableClientIncomingEvents.HAND_FINISHED]: TableHandFinishedPayload;
  [TableClientIncomingEvents.PLAYER_TURN]: TablePlayerTurnPayload;
  [TableClientIncomingEvents.ACTION_PERFORMED]: TableActionPerformedPayload;
  [TableClientIncomingEvents.SIT_OUT_RESPONSE]: TableSitPayload;
  [TableClientIncomingEvents.SIT_IN_RESPONSE]: TableSitPayload;
  [TableClientIncomingEvents.ERROR]: TableErrorPayload;
  [TableClientIncomingEvents.DUPLICATE_TABLE_CONNECTION]: TableStatePayload;
  [TableClientIncomingEvents.ADDING_CHIPS]: TableAddingChipsPayload;
  [TableClientIncomingEvents.ADDING_CHIPS_END]: TableAddingChipsEndPayload;
  [TableClientIncomingEvents.TOURNAMENT_BREAK_STARTED]: TableTournamentBreakPayload;
  [TableClientIncomingEvents.TOURNAMENT_BREAK_ENDED]: TableTournamentBreakPayload;
  [TableClientIncomingEvents.TOURNAMENT_WAITING_FOR_BREAK]: TableTournamentBreakPayload;
  [TableClientIncomingEvents.TOURNAMENT_PAUSED]: TableTournamentPausedPayload;
  [TableClientIncomingEvents.TOURNAMENT_RESUMING_AT]: TableTournamentResumingAtPayload;
  [TableClientIncomingEvents.TOURNAMENT_RESUMED]: TableTournamentResumedPayload;
  [TableClientIncomingEvents.TOURNAMENT_HAND_BY_HAND_START_NOTICE]: null;
  [TableClientIncomingEvents.TOURNAMENT_IN_THE_MONEY_NOTICE]: null;
  [TableClientIncomingEvents.SATELLITE_TOURNAMENT_ENDED]: TableSatelliteTournamentEndedPayload;
  [TableClientIncomingEvents.NEW_VERSION_AVAILABLE]: null;
  [SharedTournamentIncomingEvents.TOURNAMENT_LEVEL_CHANGE]: TableTournamentLevelChangePayload;
  [SharedTournamentIncomingEvents.TOURNAMENT_CANCELLED]: TableTournamentCancelledPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_PLAYER_ELIMINATED]: TournamentPlayerEliminatedPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_ENDED]: TableTournamentEndedPayload;
};

export type TableEvent = {
  [EventType in keyof TableEventPayloads]: EmitTableEvent<EventType, TableEventPayloads[EventType]>;
}[keyof TableEventPayloads];
export type TableIncomingSocketEvent = UserTableEvent | TableEvent;

type UserLobbyEventPayloads = {
  [LobbyClientIncomingEvents.DUPLICATE_LOGIN]: LobbyDuplicateLoginPayload;
  [LobbyClientIncomingEvents.CANCEL_SEAT_READY]: LobbyCancelSeatReadyPayload;
  [LobbyClientIncomingEvents.LOBBY_SEAT_READY]: SeatReadyEventTableInfo;
  [LobbyClientIncomingEvents.USER_TOURNAMENT_DOLLARS_ADDED]: LobbyUserTournamentDollarsAddedPayload;
  [LobbyClientIncomingEvents.USER_TOURNAMENT_TICKET_ADDED]: LobbyUserTournamentTicketAddedPayload;
  [LobbyClientIncomingEvents.TABLE_URL_UPDATED]: LobbyTableUrlUpdatedPayload;
  [TableClientIncomingEvents.TABLE_SEAT_READY]: SeatReadyEventTableInfo;
  [TournamentsClientIncomingEvents.TOURNAMENT_START]: TournamentStartPayload;
};

export type UserLobbyEvent = {
  [EventType in keyof UserLobbyEventPayloads]: EmitUserEvent<EventType, UserLobbyEventPayloads[EventType]>;
}[keyof UserLobbyEventPayloads];

type LobbyEventPayloads = {
  [LobbyClientIncomingEvents.LOBBY_ALERT_UPDATED]: LobbyAlertUpdatedPayload;
  [LobbyClientIncomingEvents.MAINTENANCE_CONFIG_UPDATED]: LobbyMaintenanceConfigUpdatedPayload;
  [LobbyClientIncomingEvents.GLOBAL_SETTINGS_UPDATED]: GlobalSettingsUpdatedEvent['setting'];
  [LobbyClientIncomingEvents.TOURNAMENT_SETTING_DELETED]: LobbyTournamentSettingDeletedPayload;
  [LobbyClientIncomingEvents.TOURNAMENT_SETTING_UPDATED]: TournamentSettingUpdatedEvent;
  [LobbyClientIncomingEvents.LEADERBOARD_STATE_UPDATE]: ActiveLeaderboardOverview;
  [LobbyClientIncomingEvents.NEW_LEADERBOARD_STARTED]: ActiveLeaderboardOverview;
  [LobbyClientIncomingEvents.JOIN_LEADERBOARD_ROOM_SUCCESS]: LobbyJoinLeaderboardRoomSuccessPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_STATS_LOBBY]: TournamentStatsLobbyPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_ENDED]: TournamentEndedPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_CREATED]: TournamentCreatedPayload;
  [SharedTournamentIncomingEvents.TOURNAMENT_CANCELLED]: TournamentCancelledPayload;
};

export type LobbyEvent = {
  [EventType in keyof LobbyEventPayloads]: EmitEvent<EventType, LobbyEventPayloads[EventType]>;
}[keyof LobbyEventPayloads];

export type LobbyIncomingSocketEvent = LobbyEvent | UserLobbyEvent;

type TournamentRoomEventPayloads = {
  [TournamentsClientIncomingEvents.TOURNAMENT_REGISTRATION_SUCCESS]: TournamentRegistrationSuccessPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_UNREGISTRATION_SUCCESS]: TournamentUnregistrationSuccessPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_PLAYER_ELIMINATED]: TournamentPlayerEliminatedPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_ENDED]: TournamentEndedPayload;
  [TournamentsClientIncomingEvents.TOURNAMENT_STATS_LOBBY]: TournamentStatsLobbyPayload;
  [SharedTournamentIncomingEvents.TOURNAMENT_LEVEL_CHANGE]: TournamentLevelChangePayload;
  [SharedTournamentIncomingEvents.TOURNAMENT_CANCELLED]: TournamentCancelledPayload;
};

export type TournamentRoomEvent = {
  [EventType in keyof TournamentRoomEventPayloads]: EmitTournamentRoomEvent<
    EventType,
    TournamentRoomEventPayloads[EventType]
  >;
}[keyof TournamentRoomEventPayloads];

type UserTableRequestEventPayloads = {
  [TableClientIncomingEvents.CONFIRM]: TableConfirmPayload;
  [TableClientIncomingEvents.MY_TURN]: TableMyTurnPayload;
  [TableClientIncomingEvents.RUNOUT_COUNT_REQUEST]: TableRunoutCountRequestPayload;
  [TableClientIncomingEvents.RUNOUT_AGREEMENT_REQUEST]: TableRunoutAgreementRequestPayload;
};

export type UserTableRequestEvent = {
  [EventType in keyof UserTableRequestEventPayloads]: EmitEvent<EventType, UserTableRequestEventPayloads[EventType]>;
}[keyof UserTableRequestEventPayloads];

export enum LobbyClientOutgoingEvents {
  JOIN_TOURNAMENT_ROOM = 'JOIN_TOURNAMENT_ROOM',
  LEAVE_TOURNAMENT_ROOM = 'LEAVE_TOURNAMENT_ROOM',
  JOIN_LEADERBOARD_ROOM = 'JOIN_LEADERBOARD_ROOM',
  LEAVE_LEADERBOARD_ROOM = 'LEAVE_LEADERBOARD_ROOM',
}

export enum LobbyClientIncomingEvents {
  DUPLICATE_LOGIN = 'DUPLICATE_LOGIN',
  CANCEL_SEAT_READY = 'CANCEL_SEAT_READY',
  MAINTENANCE_CONFIG_UPDATED = 'MAINTENANCE_CONFIG_UPDATED',
  LOBBY_ALERT_UPDATED = 'LOBBY_ALERT_UPDATED',
  GLOBAL_SETTINGS_UPDATED = 'GLOBAL_SETTINGS_UPDATED',
  TOURNAMENT_SETTING_DELETED = 'TOURNAMENT_SETTING_DELETED',
  TOURNAMENT_SETTING_UPDATED = 'TOURNAMENT_SETTING_UPDATED',
  LOBBY_SEAT_READY = 'LOBBY_SEAT_READY',
  USER_TOURNAMENT_DOLLARS_ADDED = 'USER_TOURNAMENT_DOLLARS_ADDED',
  USER_TOURNAMENT_TICKET_ADDED = 'USER_TOURNAMENT_TICKET_ADDED',
  TABLE_URL_UPDATED = 'TABLE_URL_UPDATED',
  NEW_LEADERBOARD_STARTED = 'NEW_LEADERBOARD_STARTED',
  LEADERBOARD_STATE_UPDATE = 'LEADERBOARD_STATE_UPDATE',
  JOIN_LEADERBOARD_ROOM_SUCCESS = 'JOIN_LEADERBOARD_ROOM_SUCCESS',
}

export enum TournamentsClientIncomingEvents {
  TOURNAMENT_START = 'TOURNAMENT_START',
  TOURNAMENT_REGISTRATION_SUCCESS = 'TOURNAMENT_REGISTRATION_SUCCESS',
  TOURNAMENT_UNREGISTRATION_SUCCESS = 'TOURNAMENT_UNREGISTRATION_SUCCESS',
  TOURNAMENT_PLAYER_ELIMINATED = 'TOURNAMENT_PLAYER_ELIMINATED',
  TOURNAMENT_ENDED = 'TOURNAMENT_ENDED',
  TOURNAMENT_CREATED = 'TOURNAMENT_CREATED',
  TOURNAMENT_STATS_LOBBY = 'TOURNAMENT_STATS_LOBBY',
}

export enum SharedTournamentIncomingEvents {
  TOURNAMENT_LEVEL_CHANGE = 'TOURNAMENT_LEVEL_CHANGE',
  TOURNAMENT_CANCELLED = 'TOURNAMENT_CANCELLED',
}

export enum ChatClientIncomingEvents {
  SYSTEM_MESSAGE = 'SYSTEM_MESSAGE',
  NEW_MESSAGE = 'NEW_MESSAGE',
}

export enum ChatClientOutgoingEvents {
  SEND_MESSAGE = 'SEND_MESSAGE',
}

export enum TableClientOutgoingEvents {
  PLAYER_ACTION = 'PLAYER_ACTION',
  SIT_OUT = 'SIT_OUT',
  SIT_OUT_NEXT_BB = 'SIT_OUT_NEXT_BB',
  SIT_IN = 'SIT_IN',
  SIT_OUT_ALL_TABLES = 'SIT_OUT_ALL_TABLES',
  SIT_OUT_NEXT_BB_ALL_TABLES = 'SIT_OUT_NEXT_BB_ALL_TABLES',
  SIT_IN_ALL_TABLES = 'SIT_IN_ALL_TABLES',
  CANCEL_SIT_OUT_NEXT_BB_ALL_TABLES = 'CANCEL_SIT_OUT_NEXT_BB_ALL_TABLES',
}

export enum TableClientIncomingEvents {
  TABLE_STATE = 'TABLE_STATE',
  TABLE_JOINED = 'TABLE_JOINED',
  SEAT_RESERVED = 'SEAT_RESERVED',
  TABLE_LEFT = 'TABLE_LEFT',
  HAND_FINISHED = 'HAND_FINISHED',
  PLAYER_TURN = 'PLAYER_TURN',
  ACTION_PERFORMED = 'ACTION_PERFORMED',
  SIT_OUT_RESPONSE = 'SIT_OUT_RESPONSE',
  SIT_IN_RESPONSE = 'SIT_IN_RESPONSE',
  ERROR = 'ERROR',
  CLIENT_VERSION_OUTDATED = 'CLIENT_VERSION_OUTDATED',
  DUPLICATE_TABLE_CONNECTION = 'DUPLICATE_TABLE_CONNECTION',
  REBUY_PROCESSED = 'REBUY_PROCESSED',
  AUTO_TOP_UP_BALANCE_UPDATED = 'AUTO_TOP_UP_BALANCE_UPDATED',
  CONFIRM = 'CONFIRM',
  MY_TURN = 'MY_TURN',
  // Run-it-multiple events
  RUNOUT_COUNT_REQUEST = 'RUNOUT_COUNT_REQUEST',
  RUNOUT_AGREEMENT_REQUEST = 'RUNOUT_AGREEMENT_REQUEST',
  RUNOUT_WAITING = 'RUNOUT_WAITING',
  ADDING_CHIPS = 'ADDING_CHIPS',
  ADDING_CHIPS_END = 'ADDING_CHIPS_END',
  // Tournament events
  TOURNAMENT_BREAK_STARTED = 'TOURNAMENT_BREAK_STARTED',
  TOURNAMENT_BREAK_ENDED = 'TOURNAMENT_BREAK_ENDED',
  TOURNAMENT_WAITING_FOR_BREAK = 'TOURNAMENT_WAITING_FOR_BREAK',
  TOURNAMENT_PAUSED = 'TOURNAMENT_PAUSED',
  TOURNAMENT_RESUMING_AT = 'TOURNAMENT_RESUMING_AT',
  TOURNAMENT_RESUMED = 'TOURNAMENT_RESUMED',
  TOURNAMENT_HAND_BY_HAND_START_NOTICE = 'TOURNAMENT_HAND_BY_HAND_START_NOTICE',
  TOURNAMENT_IN_THE_MONEY_NOTICE = 'TOURNAMENT_IN_THE_MONEY_NOTICE',
  TABLE_SEAT_READY = 'TABLE_SEAT_READY',
  CHANGE_TABLE = 'CHANGE_TABLE',
  SATELLITE_TOURNAMENT_ENDED = 'SATELLITE_TOURNAMENT_ENDED',
  NEW_VERSION_AVAILABLE = 'NEW_VERSION_AVAILABLE',
}

export enum PlayerAction {
  FOLD = 'FOLD',
  CHECK = 'CHECK',
  CALL = 'CALL',
  BET = 'BET',
  RAISE = 'RAISE',
  BRING_IN = 'BRING IN',
  COMPLETE = 'COMPLETE',
  DISCARD = 'DISCARD',
  TIMEBANK = 'TIMEBANK',
  STAND_PAT = 'STAND PAT',
  REVEAL_FOLDED_CARDS = 'REVEAL_FOLDED_CARDS',
  LEAVE_TABLE = 'LEAVE_TABLE',
  SET_WAIT_FOR_BIG_BLIND = 'SET_WAIT_FOR_BIG_BLIND',
  CONFIRM_SEAT = 'CONFIRM_SEAT',
  START_ADDING_CHIPS = 'START_ADDING_CHIPS',
  SET_TIMEBANK_AUTO_ENABLED_SERVER = 'SET_TIMEBANK_AUTO_ENABLED_SERVER',
  RUNOUT_COUNT_RESPONSE = 'RUNOUT_COUNT_RESPONSE',
  RUNOUT_AGREEMENT_RESPONSE = 'RUNOUT_AGREEMENT_RESPONSE',
  SIT_OUT = 'SIT_OUT',
  SIT_OUT_NEXT_BB = 'SIT_OUT_NEXT_BB',
  SIT_IN = 'SIT_IN',
}
